// frontend/src/pages/ComparePage.js
import React from 'react';
import { Link } from 'react-router-dom';
import { useCompare } from '../context/CompareContext';
import helpers from '../utils/helpers';
import { FaTimes, FaCheck, FaMinus, FaArrowLeft, FaCar } from 'react-icons/fa';

function ComparePage() {
  const { compareList, removeFromCompare, clearCompare } = useCompare();

  if (compareList.length === 0) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center bg-gray-50 px-4 text-center">
        <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-200 max-w-md">
          <FaCar className="text-gray-300 text-6xl mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">No Vehicles Selected</h2>
          <p className="text-gray-600 mb-6">
            Select up to 3 vehicles from the marketplace to compare their specs side-by-side.
          </p>
          <Link 
            to="/vehicles/browse" 
            className="bg-primary hover:bg-primary-dark text-white font-bold py-3 px-6 rounded-lg transition duration-300 inline-block"
          >
            Browse Vehicles
          </Link>
        </div>
      </div>
    );
  }

  // Helper to render a row
  const renderRow = (label, field, isFeature = false) => (
    <tr className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
      <td className="p-4 font-bold text-gray-600 bg-gray-50 w-1/4 align-top">{label}</td>
      {compareList.map((vehicle) => (
        <td key={vehicle.id} className="p-4 text-gray-800 align-top w-1/4">
          {isFeature ? (
             // Check if feature string exists in the vehicle's feature list
             vehicle.features && vehicle.features.some(f => f.toLowerCase().includes(field.toLowerCase())) 
             ? <span className="text-green-600 flex items-center gap-1"><FaCheck size={12} /> Yes</span> 
             : <span className="text-gray-400 flex items-center gap-1"><FaMinus size={12} /> No</span>
          ) : (
             // Standard Field
             field === 'price' ? helpers.formatCurrency(vehicle.price, 'NGN') :
             field === 'mileage' ? `${vehicle.mileage.toLocaleString()} km` :
             field === 'condition' ? <span className="capitalize">{vehicle.condition.replace('_', ' ')}</span> :
             vehicle[field] || '-'
          )}
        </td>
      ))}
      {/* Fill empty columns if less than 3 items */}
      {[...Array(3 - compareList.length)].map((_, i) => (
        <td key={`empty-${i}`} className="p-4 bg-gray-50/30"></td>
      ))}
    </tr>
  );

  return (
    <div className="bg-white min-h-screen py-8">
      <div className="container mx-auto px-4">
        
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center gap-4">
            <Link to="/vehicles/browse" className="text-gray-500 hover:text-primary flex items-center gap-2">
                <FaArrowLeft /> Back to Marketplace
            </Link>
            <h1 className="text-2xl font-bold text-gray-800">Vehicle Comparison</h1>
          </div>
          <button 
            onClick={clearCompare}
            className="text-red-500 hover:text-red-700 text-sm font-bold"
          >
            Clear All
          </button>
        </div>

        <div className="overflow-x-auto border border-gray-200 rounded-lg shadow-sm">
          <table className="w-full text-left border-collapse min-w-[800px]">
            <thead>
              <tr>
                <th className="p-4 bg-gray-50 w-1/4"></th>
                {compareList.map((vehicle) => (
                  <th key={vehicle.id} className="p-4 w-1/4 relative bg-white border-b border-gray-200 align-top">
                    <button 
                      onClick={() => removeFromCompare(vehicle.id)}
                      className="absolute top-2 right-2 text-gray-400 hover:text-red-500 p-1"
                      title="Remove"
                    >
                      <FaTimes />
                    </button>
                    <div className="h-32 bg-gray-100 rounded-lg overflow-hidden mb-3">
                      <img 
                        src={vehicle.image || 'https://via.placeholder.com/300'} 
                        alt={vehicle.model} 
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <h3 className="font-bold text-lg text-gray-900 leading-tight mb-1">
                      {vehicle.year} {vehicle.make} {vehicle.model}
                    </h3>
                    <Link 
                      to={`/vehicles/${vehicle.id}`} 
                      className="text-primary text-sm hover:underline"
                    >
                      View Details
                    </Link>
                  </th>
                ))}
                {[...Array(3 - compareList.length)].map((_, i) => (
                  <th key={`empty-head-${i}`} className="p-4 w-1/4 bg-gray-50/30 border-b border-gray-200 text-center text-gray-400 font-normal">
                    <div className="h-32 border-2 border-dashed border-gray-200 rounded-lg flex items-center justify-center mb-2">
                        Add Vehicle
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {renderRow('Price', 'price')}
              {renderRow('Condition', 'condition')}
              {renderRow('Mileage', 'mileage')}
              {renderRow('Transmission', 'transmission')}
              {renderRow('Year', 'year')}
              
              {/* Feature Comparison Header */}
              <tr>
                <td colSpan={4} className="p-3 bg-gray-100 font-bold text-gray-700 text-sm uppercase tracking-wide">
                    Key Features
                </td>
              </tr>
              
              {/* Common Features to Check */}
              {renderRow('Leather Seats', 'leather', true)}
              {renderRow('Sunroof', 'sunroof', true)}
              {renderRow('Reverse Camera', 'camera', true)}
              {renderRow('Bluetooth', 'bluetooth', true)}
              {renderRow('Navigation', 'navigation', true)}
              {renderRow('Alloy Wheels', 'alloy', true)}
            </tbody>
          </table>
        </div>

      </div>
    </div>
  );
}

export default ComparePage;